import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Copy,
  Download,
  File,
  FilePdf,
  Image as ImageIcon,
  MagnifyingGlass,
  Trash,
  Upload,
} from '@phosphor-icons/react';

import { DashboardLayout } from '../components/DashboardLayout';
import { API, BACKEND_URL, formatApiErrorDetail } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const FILE_KIND_OPTIONS = [
  { value: 'all', label: 'Alle Typen' },
  { value: 'image', label: 'Bilder' },
  { value: 'pdf', label: 'PDF' },
  { value: 'text', label: 'Text / Daten' },
  { value: 'file', label: 'Sonstige' },
];

const USAGE_CONTEXT_OPTIONS = [
  { value: 'general', label: 'Allgemein' },
  { value: 'template-background', label: 'Template Hintergrund' },
  { value: 'template-logo', label: 'Template Logo' },
  { value: 'campaign-asset', label: 'Kampagnen-Asset' },
  { value: 'form-attachment', label: 'Formular / Attachment' },
];

const INITIAL_UPLOAD_FORM = {
  folder: 'general',
  usageContext: 'general',
  departmentId: 'none',
  campaignId: 'none',
  domainId: 'none',
  templateId: 'none',
  title: '',
};

export function MediaLibraryPage() {
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [options, setOptions] = useState({ allowed_types: [], max_file_size: 0 });
  const [references, setReferences] = useState({ departments: [], campaigns: [], domains: [], templates: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadForm, setUploadForm] = useState(INITIAL_UPLOAD_FORM);

  const fetchReferenceData = useCallback(async () => {
    const [departments, campaigns, domains, templates] = await Promise.allSettled([
      axios.get(`${API}/departments`, { withCredentials: true }),
      axios.get(`${API}/campaigns`, { withCredentials: true }),
      axios.get(`${API}/domains`, { withCredentials: true }),
      axios.get(`${API}/templates`, { withCredentials: true }),
    ]);
    setReferences({
      departments: departments.status === 'fulfilled' ? departments.value.data : [],
      campaigns: campaigns.status === 'fulfilled' ? campaigns.value.data : [],
      domains: domains.status === 'fulfilled' ? domains.value.data : [],
      templates: templates.status === 'fulfilled' ? templates.value.data : [],
    });
  }, []);

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (selectedFolder !== 'all') params.folder = selectedFolder;
      if (kindFilter !== 'all') params.kind = kindFilter;
      if (search.trim()) params.search = search.trim();

      const [mediaResponse, foldersResponse, optionsResponse] = await Promise.all([
        axios.get(`${API}/media`, { params, withCredentials: true }),
        axios.get(`${API}/media/folders`, { withCredentials: true }),
        axios.get(`${API}/media/options`, { withCredentials: true }),
      ]);

      setFiles(mediaResponse.data.items || []);
      setFolders(foldersResponse.data.folders || []);
      setOptions(optionsResponse.data || { allowed_types: [], max_file_size: 0 });
      setSelectedFile((current) => (current ? (mediaResponse.data.items || []).find((item) => item.id === current.id) || null : null));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Media Library konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [kindFilter, search, selectedFolder]);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      fetchMedia();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [fetchMedia]);

  const allowedTypesLabel = useMemo(() => options.allowed_types?.join(', ') || '—', [options.allowed_types]);
  const selectedFileUrl = selectedFile ? `${BACKEND_URL}${selectedFile.url}` : '';

  const formatFileSize = (bytes = 0) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isImage = (file) => file?.kind === 'image' || file?.content_type?.startsWith('image/');
  const isPdf = (file) => file?.kind === 'pdf' || file?.content_type === 'application/pdf';

  const getFilePreview = (file) => {
    if (isImage(file)) {
      return <img src={`${BACKEND_URL}${file.url}`} alt={file.original_filename} className="h-12 w-12 rounded-sm object-cover border border-zinc-200" data-testid={`media-thumbnail-${file.id}`} />;
    }
    if (isPdf(file)) {
      return <div className="h-12 w-12 rounded-sm border border-zinc-200 flex items-center justify-center bg-zinc-50"><FilePdf className="w-6 h-6 text-zinc-500" /></div>;
    }
    return <div className="h-12 w-12 rounded-sm border border-zinc-200 flex items-center justify-center bg-zinc-50"><File className="w-6 h-6 text-zinc-500" /></div>;
  };

  const relationSummary = (file) => {
    const relationIds = file?.relation_ids || {};
    const counts = [
      relationIds.campaign_ids?.length || 0,
      relationIds.domain_ids?.length || 0,
      relationIds.template_ids?.length || 0,
    ].reduce((sum, value) => sum + value, 0);
    return counts > 0 ? `${counts} Verknüpfung(en)` : '—';
  };

  const handleCopyUrl = async (file) => {
    await navigator.clipboard.writeText(`${BACKEND_URL}${file.url}`);
    toast.success('Datei-URL kopiert.');
  };

  const handleUploadSubmit = async () => {
    if (uploadFiles.length === 0) {
      toast.error('Bitte zuerst mindestens eine Datei auswählen.');
      return;
    }
    setUploading(true);
    let uploadedCount = 0;
    let duplicateCount = 0;
    try {
      for (const file of uploadFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', uploadForm.folder);
        formData.append('usage_context', uploadForm.usageContext);
        if (uploadForm.title.trim()) formData.append('title', uploadForm.title.trim());
        if (uploadForm.departmentId !== 'none') formData.append('department_id', uploadForm.departmentId);
        if (uploadForm.campaignId !== 'none') formData.append('campaign_id', uploadForm.campaignId);
        if (uploadForm.domainId !== 'none') formData.append('domain_id', uploadForm.domainId);
        if (uploadForm.templateId !== 'none') formData.append('template_id', uploadForm.templateId);

        const { data } = await axios.post(`${API}/media/upload`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (data.duplicate_detected) duplicateCount += 1;
        else uploadedCount += 1;
      }

      if (uploadedCount > 0) toast.success(`${uploadedCount} Datei(en) erfolgreich hochgeladen.`);
      if (duplicateCount > 0) toast.info(`${duplicateCount} Dublette(n) erkannt und vorhandene Datei wiederverwendet.`);

      setUploadFiles([]);
      setUploadForm(INITIAL_UPLOAD_FORM);
      setShowUploadDialog(false);
      fetchMedia();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Upload fehlgeschlagen.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/media/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Datei gelöscht.');
      if (selectedFile?.id === deleteTarget.id) setSelectedFile(null);
      setDeleteTarget(null);
      fetchMedia();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Löschen fehlgeschlagen.');
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="media-library-page">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Media Library</p>
            <h1 className="text-4xl font-black tracking-tight text-zinc-950">Assets</h1>
            <p className="mt-2 max-w-3xl text-sm text-zinc-600">
              Bilder, Logos, PDFs und Daten-Dateien zentral hochladen, sauber verschlagworten und direkt für Templates,
              Kampagnen und spätere Formularmodule wiederverwenden.
            </p>
          </div>
          <Button
            onClick={() => setShowUploadDialog(true)}
            className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90"
            data-testid="media-upload-open-button"
          >
            <Upload className="w-4 h-4" />
            Dateien hochladen
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-3" data-testid="media-library-summary-grid">
          <div className="border border-zinc-200 bg-white p-5 rounded-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Dateitypen</p>
            <p className="mt-3 text-sm text-zinc-700 break-words" data-testid="media-allowed-types-value">{allowedTypesLabel}</p>
          </div>
          <div className="border border-zinc-200 bg-white p-5 rounded-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Größenlimit</p>
            <p className="mt-3 text-lg font-semibold text-zinc-950" data-testid="media-size-limit-value">{formatFileSize(options.max_file_size || 0)}</p>
          </div>
          <div className="border border-zinc-200 bg-white p-5 rounded-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Dublettenstrategie</p>
            <p className="mt-3 text-sm text-zinc-700" data-testid="media-duplicate-strategy-value">Bestehende Datei per Checksumme wiederverwenden</p>
          </div>
        </div>

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.6fr)_360px] xl:items-start">
          <div className="space-y-4 min-w-0">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px_220px]">
              <div className="relative">
                <MagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 w-4 h-4" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Dateien durchsuchen..."
                  className="pl-9 border-zinc-300"
                  data-testid="media-search-input"
                />
              </div>
              <Select value={selectedFolder} onValueChange={setSelectedFolder}>
                <SelectTrigger data-testid="media-folder-filter">
                  <SelectValue placeholder="Ordner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Ordner</SelectItem>
                  {folders.map((folder) => (
                    <SelectItem key={folder.name} value={folder.name}>{folder.name} ({folder.count})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={kindFilter} onValueChange={setKindFilter}>
                <SelectTrigger data-testid="media-kind-filter">
                  <SelectValue placeholder="Typ" />
                </SelectTrigger>
                <SelectContent>
                  {FILE_KIND_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {error ? (
              <div className="border border-[#E63946] bg-white p-6 rounded-sm" data-testid="media-error-state">
                <p className="text-sm font-medium text-[#E63946]">{error}</p>
                <Button variant="outline" className="mt-4" onClick={fetchMedia} data-testid="media-retry-button">Erneut laden</Button>
              </div>
            ) : loading ? (
              <div className="space-y-3" data-testid="media-loading-state">
                {[1, 2, 3, 4, 5].map((item) => (
                  <div key={item} className="h-16 animate-pulse rounded-sm border border-zinc-200 bg-zinc-100" />
                ))}
              </div>
            ) : files.length === 0 ? (
              <div className="border border-zinc-200 bg-white p-10 rounded-sm text-center" data-testid="media-empty-state">
                <ImageIcon className="w-12 h-12 mx-auto text-zinc-300" />
                <h2 className="mt-4 text-lg font-bold text-zinc-950">Noch keine Dateien gefunden</h2>
                <p className="mt-2 text-sm text-zinc-600">Lege die erste Datei an und nutze sie danach direkt in Templates.</p>
                <Button className="mt-6 bg-[#002FA7] text-white hover:bg-[#002FA7]/90" onClick={() => setShowUploadDialog(true)} data-testid="media-empty-upload-button">
                  <Upload className="w-4 h-4" />
                  Erste Datei hochladen
                </Button>
              </div>
            ) : (
              <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden" data-testid="media-list-table">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[980px]">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
                        <th className="px-4 py-3">Datei</th>
                        <th className="px-4 py-3">Ordner</th>
                        <th className="px-4 py-3">Typ</th>
                        <th className="px-4 py-3">Größe</th>
                        <th className="px-4 py-3">Verknüpfungen</th>
                        <th className="px-4 py-3">Angelegt</th>
                        <th className="px-4 py-3 text-right">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {files.map((file) => (
                        <tr
                          key={file.id}
                          className={`border-b border-zinc-100 last:border-b-0 cursor-pointer ${selectedFile?.id === file.id ? 'bg-[#002FA7]/5' : 'hover:bg-zinc-50'}`}
                          onClick={() => setSelectedFile(file)}
                          data-testid={`media-row-${file.id}`}
                        >
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              {getFilePreview(file)}
                              <div className="min-w-0">
                                <p className="font-semibold text-zinc-950 truncate" data-testid={`media-filename-${file.id}`}>{file.original_filename}</p>
                                <p className="text-xs text-zinc-500 truncate">{file.title || file.content_type}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-zinc-700">{file.folder}</td>
                          <td className="px-4 py-4 text-sm text-zinc-700">{file.kind}</td>
                          <td className="px-4 py-4 text-sm text-zinc-700">{formatFileSize(file.size)}</td>
                          <td className="px-4 py-4 text-sm text-zinc-700">{relationSummary(file)}</td>
                          <td className="px-4 py-4 text-sm text-zinc-700">{new Date(file.created_at).toLocaleString('de-DE')}</td>
                          <td className="px-4 py-4">
                            <div className="flex justify-end gap-2">
                              <Button variant="outline" onClick={(event) => { event.stopPropagation(); handleCopyUrl(file); }} data-testid={`media-copy-button-${file.id}`}>
                                <Copy className="w-4 h-4" />
                                URL
                              </Button>
                              <Button variant="outline" onClick={(event) => { event.stopPropagation(); setDeleteTarget(file); }} data-testid={`media-delete-button-${file.id}`}>
                                <Trash className="w-4 h-4 text-[#E63946]" />
                                Löschen
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <aside className="border border-zinc-200 bg-white rounded-sm p-5 xl:sticky xl:top-8" data-testid="media-detail-panel">
            {selectedFile ? (
              <div className="space-y-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Dateidetails</p>
                  <h2 className="mt-2 text-xl font-bold text-zinc-950 break-words" data-testid="media-detail-filename">{selectedFile.original_filename}</h2>
                </div>

                <div className="border border-zinc-200 bg-zinc-50 rounded-sm overflow-hidden">
                  {isImage(selectedFile) ? (
                    <img src={selectedFileUrl} alt={selectedFile.original_filename} className="w-full aspect-[4/3] object-contain bg-white" data-testid="media-image-preview" />
                  ) : isPdf(selectedFile) ? (
                    <iframe src={selectedFileUrl} title={selectedFile.original_filename} className="w-full aspect-[4/3] bg-white" data-testid="media-pdf-preview" />
                  ) : (
                    <div className="aspect-[4/3] flex flex-col items-center justify-center gap-3 bg-white text-zinc-500" data-testid="media-generic-preview">
                      <File className="w-10 h-10" />
                      <p className="text-sm">Für diesen Dateityp ist keine Inline-Vorschau hinterlegt.</p>
                    </div>
                  )}
                </div>

                <div className="grid gap-3 text-sm">
                  <div><span className="text-zinc-500">Typ:</span> <span className="font-medium text-zinc-950" data-testid="media-detail-type">{selectedFile.content_type}</span></div>
                  <div><span className="text-zinc-500">Größe:</span> <span className="font-medium text-zinc-950" data-testid="media-detail-size">{formatFileSize(selectedFile.size)}</span></div>
                  <div><span className="text-zinc-500">Ordner:</span> <span className="font-medium text-zinc-950" data-testid="media-detail-folder">{selectedFile.folder}</span></div>
                  <div><span className="text-zinc-500">Checksumme:</span> <span className="font-medium text-zinc-950 break-all" data-testid="media-detail-checksum">{selectedFile.checksum || '—'}</span></div>
                  <div><span className="text-zinc-500">Verwendung:</span> <span className="font-medium text-zinc-950" data-testid="media-detail-usage">{selectedFile.usage_contexts?.join(', ') || '—'}</span></div>
                  <div><span className="text-zinc-500">Kampagnen:</span> <span className="font-medium text-zinc-950" data-testid="media-detail-campaign-relations">{selectedFile.relation_ids?.campaign_ids?.join(', ') || '—'}</span></div>
                  <div><span className="text-zinc-500">Domains:</span> <span className="font-medium text-zinc-950" data-testid="media-detail-domain-relations">{selectedFile.relation_ids?.domain_ids?.join(', ') || '—'}</span></div>
                  <div><span className="text-zinc-500">Templates:</span> <span className="font-medium text-zinc-950" data-testid="media-detail-template-relations">{selectedFile.relation_ids?.template_ids?.join(', ') || '—'}</span></div>
                </div>

                <div className="grid gap-2">
                  <Button variant="outline" onClick={() => handleCopyUrl(selectedFile)} data-testid="media-detail-copy-button">
                    <Copy className="w-4 h-4" />
                    URL kopieren
                  </Button>
                  <Button variant="outline" asChild data-testid="media-detail-download-button">
                    <a href={selectedFileUrl} target="_blank" rel="noopener noreferrer">
                      <Download className="w-4 h-4" />
                      Datei öffnen / herunterladen
                    </a>
                  </Button>
                  <Button variant="outline" onClick={() => setDeleteTarget(selectedFile)} data-testid="media-detail-delete-button">
                    <Trash className="w-4 h-4 text-[#E63946]" />
                    Löschen
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-zinc-600" data-testid="media-detail-empty-state">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Dateidetails</p>
                <p className="mt-4">Wähle links eine Datei aus, um Vorschau, Metadaten und Verknüpfungen zu sehen.</p>
              </div>
            )}
          </aside>
        </div>
      </div>

      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-3xl rounded-sm border-2 border-zinc-950 shadow-[8px_8px_0px_0px_rgba(9,9,11,1)]" data-testid="media-upload-dialog">
          <DialogHeader>
            <DialogTitle data-testid="media-upload-dialog-title">Dateien hochladen</DialogTitle>
            <DialogDescription data-testid="media-upload-dialog-description">Dateien werden validiert, in Object Storage gespeichert und mit Metadaten, Verknüpfungen und Dublettenprüfung erfasst.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-5 py-2 lg:grid-cols-[minmax(0,1fr)_280px]">
            <div className="space-y-4">
              <label className="flex min-h-[180px] cursor-pointer flex-col items-center justify-center rounded-sm border-2 border-dashed border-zinc-300 bg-zinc-50 px-6 py-8 text-center hover:border-[#002FA7] hover:bg-[#002FA7]/5" data-testid="media-upload-dropzone">
                <Upload className="w-8 h-8 text-zinc-500" />
                <span className="mt-3 text-sm font-medium text-zinc-950">Dateien auswählen</span>
                <span className="mt-1 text-xs text-zinc-500">Erlaubt: {allowedTypesLabel}</span>
                <span className="text-xs text-zinc-500">Maximal: {formatFileSize(options.max_file_size || 0)}</span>
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(event) => setUploadFiles(Array.from(event.target.files || []))}
                  data-testid="media-upload-input"
                />
              </label>

              <div className="border border-zinc-200 rounded-sm bg-white p-4" data-testid="media-upload-file-list">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Ausgewählte Dateien</p>
                {uploadFiles.length === 0 ? (
                  <p className="mt-3 text-sm text-zinc-500">Noch keine Dateien ausgewählt.</p>
                ) : (
                  <div className="mt-3 space-y-2 max-h-48 overflow-auto">
                    {uploadFiles.map((file) => (
                      <div key={`${file.name}-${file.size}`} className="flex items-center justify-between border border-zinc-200 rounded-sm px-3 py-2 text-sm" data-testid={`upload-file-${file.name}`}>
                        <span className="truncate pr-4 text-zinc-950">{file.name}</span>
                        <span className="text-zinc-500">{formatFileSize(file.size)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <Input
                value={uploadForm.title}
                onChange={(event) => setUploadForm((current) => ({ ...current, title: event.target.value }))}
                placeholder="Optionaler Titel"
                data-testid="media-upload-title-input"
              />

              <Select value={uploadForm.folder} onValueChange={(value) => setUploadForm((current) => ({ ...current, folder: value }))}>
                <SelectTrigger data-testid="media-upload-folder-select"><SelectValue placeholder="Ordner" /></SelectTrigger>
                <SelectContent>
                  {folders.map((folder) => (
                    <SelectItem key={folder.name} value={folder.name}>{folder.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={uploadForm.usageContext} onValueChange={(value) => setUploadForm((current) => ({ ...current, usageContext: value }))}>
                <SelectTrigger data-testid="media-upload-usage-select"><SelectValue placeholder="Verwendung" /></SelectTrigger>
                <SelectContent>
                  {USAGE_CONTEXT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={uploadForm.departmentId} onValueChange={(value) => setUploadForm((current) => ({ ...current, departmentId: value }))}>
                <SelectTrigger data-testid="media-upload-department-select"><SelectValue placeholder="Abteilung" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine Abteilung</SelectItem>
                  {references.departments.map((department) => (
                    <SelectItem key={department.id} value={department.id}>{department.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={uploadForm.campaignId} onValueChange={(value) => setUploadForm((current) => ({ ...current, campaignId: value }))}>
                <SelectTrigger data-testid="media-upload-campaign-select"><SelectValue placeholder="Kampagne" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine Kampagne</SelectItem>
                  {references.campaigns.map((campaign) => (
                    <SelectItem key={campaign.id} value={campaign.id}>{campaign.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={uploadForm.domainId} onValueChange={(value) => setUploadForm((current) => ({ ...current, domainId: value }))}>
                <SelectTrigger data-testid="media-upload-domain-select"><SelectValue placeholder="Domain" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine Domain</SelectItem>
                  {references.domains.map((domain) => (
                    <SelectItem key={domain.id} value={domain.id}>{domain.base_domain}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={uploadForm.templateId} onValueChange={(value) => setUploadForm((current) => ({ ...current, templateId: value }))}>
                <SelectTrigger data-testid="media-upload-template-select"><SelectValue placeholder="Template" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Template</SelectItem>
                  {references.templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                onClick={handleUploadSubmit}
                disabled={uploading}
                className="w-full bg-[#002FA7] text-white hover:bg-[#002FA7]/90"
                data-testid="media-upload-submit-button"
              >
                <Upload className="w-4 h-4" />
                {uploading ? 'Upload läuft...' : 'Upload starten'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm border-2 border-zinc-950 shadow-[8px_8px_0px_0px_rgba(9,9,11,1)]">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="media-delete-dialog-title">Datei löschen?</AlertDialogTitle>
            <AlertDialogDescription data-testid="media-delete-dialog-description">
              {deleteTarget ? `"${deleteTarget.original_filename}" wird in der Library deaktiviert und verschwindet aus Listen.` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="media-delete-cancel-button">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-[#E63946] text-white hover:bg-[#E63946]/90"
              data-testid="media-delete-confirm-button"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
